<template>
  <b-modal
    id="modal-upload-payment-proof"
    ok-title="Submit"
    cancel-variant="outline-secondary"
    body-class="common_modal modal_visible"
    hide-footer
    style="max-height: 90vh"
    centered
    size="lg"
  >
    <template #modal-header="{ close }">
      <h3 class="m-0">{{ isEdit ? "編輯" : "上傳" }}付款憑證</h3>
      <a @click="close()">
        <font-awesome-icon style="font-size: 20px" icon="fas fa-times" />
      </a>
    </template>
    <b-overlay
      :show="loading"
      spinner-variant="primary"
      spinner-type="grow"
      spinner-small
      rounded="sm"
    >
      <validation-observer #default="{ handleSubmit }" ref="refFormObserver">
        <b-form class="mt-1" @submit.prevent="handleSubmit(onSubmit)">
          <!-- 帳單資訊顯示 -->
          <!-- <b-row class="mb-1">
            <b-col md="12">
              <b-card class="bg-light">
                <b-card-body class="p-2">
                  <div class="d-flex justify-content-between align-items-center">
                    <div>
                      <strong>帳單編號：</strong>{{ billData.bill_number || '未設定' }}
                    </div>
                    <div>
                      <strong>帳單總額：</strong>{{ formattedBillAmount }}
                    </div>
                  </div>
                </b-card-body>
              </b-card>
            </b-col>
          </b-row> -->

          <!-- 付款憑證列表 -->
          <div class="mb-3">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <h5 class="mb-2">付款憑證明細</h5>
              <div>
                <strong>帳單編號：</strong>{{ billData.bill_number || '未設定' }}
              </div>
            </div>
            <b-row
              v-for="(item, index) in paymentProofItems"
              :key="`payment-proof-${index}`"
              class="mb-3 border rounded p-3"
            >
              <b-col cols="12">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <h6 class="mb-0">付款憑證 #{{ index + 1 }}</h6>
                  <div class="d-flex align-items-center">
                    <feather-icon
                      size="16"
                      icon="PlusIcon"
                      class="cursor-pointer text-success mr-2"
                      @click="addPaymentProofItem(index)"
                    />
                    <feather-icon
                      size="16"
                      icon="XIcon"
                      v-if="paymentProofItems.length > 1"
                      class="cursor-pointer text-danger"
                      @click="removePaymentProofItem(index)"
                    />
                  </div>
                </div>

                <!-- 付款憑證編號 -->
                <b-row class="mb-2">
                  <b-col md="6">
                    <validation-provider
                      #default="validationContext"
                      name="此欄"
                      rules="required"
                    >
                      <b-form-group
                        :label="`付款憑證編號：`"
                        :label-for="`payment_proof_number_${index}`"
                        label-cols-md="4"
                        class="required"
                      >
                        <b-form-input
                          :id="`payment_proof_number_${index}`"
                          v-model="item.payment_proof_number"
                          placeholder="請輸入付款憑證編號"
                          maxlength="50"
                          :state="getValidationState(validationContext)"
                          @blur="validatePaymentProofNumber(index)"
                        />
                        <b-form-invalid-feedback>
                          {{ validationContext.errors[0] }}
                        </b-form-invalid-feedback>
                      </b-form-group>
                    </validation-provider>
                  </b-col>

                  <!-- 付款金額 -->
                  <b-col md="6">
                    <validation-provider
                      #default="validationContext"
                      name="此欄"
                      rules="required|min_value:0.01"
                    >
                      <b-form-group
                        label="付款金額："
                        :label-for="`payment_amount_${index}`"
                        label-cols-md="4"
                        class="required"
                      >
                        <b-form-input
                          :id="`payment_amount_${index}`"
                          v-model="item.payment_amount"
                          type="number"
                          step="0.01"
                          min="0.01"
                          placeholder="請輸入付款金額"
                          :state="getValidationState(validationContext)"
                          @input="calculateTotalAmount"
                        />
                        <b-form-invalid-feedback>
                          {{ validationContext.errors[0] }}
                        </b-form-invalid-feedback>
                      </b-form-group>
                    </validation-provider>
                  </b-col>
                </b-row>

                <!-- 付款日期和付款方式 -->
                <b-row class="mb-2">
                  <b-col md="6">
                    <validation-provider
                      #default="validationContext"
                      name="此欄"
                      rules="required"
                    >
                      <b-form-group
                        label="付款日期："
                        :label-for="`payment_date_${index}`"
                        label-cols-md="4"
                        class="required"
                      >
                        <flat-pickr
                          :id="`payment_date_${index}`"
                          v-model="item.payment_date"
                          :config="datePickerConfig"
                          class="form-control"
                          placeholder="請選擇付款日期"
                          :state="getValidationState(validationContext)"
                        />
                        <b-form-invalid-feedback>
                          {{ validationContext.errors[0] }}
                        </b-form-invalid-feedback>
                      </b-form-group>
                    </validation-provider>
                  </b-col>

                  <b-col md="6">
                    <validation-provider
                      #default="validationContext"
                      name="此欄"
                      rules="required"
                    >
                      <b-form-group
                        label="付款方式："
                        :label-for="`payment_method_${index}`"
                        label-cols-md="4"
                        class="required"
                      >
                        <v-select
                          :id="`payment_method_${index}`"
                          v-model="item.payment_method_id"
                          :options="paymentMethodOptions"
                          :reduce="option => option.id"
                          label="name"
                          placeholder="請選擇付款方式"
                          :state="getValidationState(validationContext)"
                        />
                        <b-form-invalid-feedback>
                          {{ validationContext.errors[0] }}
                        </b-form-invalid-feedback>
                      </b-form-group>
                    </validation-provider>
                  </b-col>
                </b-row>

                <!-- 支票號碼 (只在選擇支票時顯示) -->
                <b-row class="mb-2" v-if="item.payment_method_id == 1">
                  <b-col md="6">
                    <b-form-group
                      label="支票號碼："
                      :label-for="`check_number_${index}`"
                      label-cols-md="4"
                    >
                      <b-form-input
                        :id="`check_number_${index}`"
                        v-model="item.check_number"
                        placeholder="請輸入支票號碼"
                        maxlength="50"
                      />
                    </b-form-group>
                  </b-col>
                </b-row>

                <!-- 檔案上傳 -->
                <b-row class="mb-2">
                  <b-col cols="12">
                    <upload-file-single
                      v-on:file-uploaded="(file) => onFileUploaded(file, index)"
                      file_delete_url="/suppliers/payment-proofs"
                      :ref="`fileUpload${index}`"
                      :props_previewImage="item.previewImageData"
                      title="付款憑證"
                    ></upload-file-single>
                  </b-col>
                </b-row>
              </b-col>
            </b-row>
          </div>

          <!-- 總額顯示和驗證 -->
          <div class="mb-3 d-flex justify-content-end align-items-center">
            <div class="d-flex flex-column align-items-end">
              <span :class="totalComparisonClass">
                付款憑證總額：${{ calculatedTotal }} / 帳單總額：${{ billData.bill_total_amount || 0 }}
              </span>
              <small v-if="showAmountWarning" class="text-danger mt-1">
                付款憑證總額超過帳單總額，請檢查明細項目
              </small>
            </div>
          </div>

          <!-- 提交按鈕 -->
          <div class="d-flex justify-content-end">
            <b-button
              type="submit"
              variant="primary"
              :disabled="loading || showAmountWarning"
              class="mr-1"
            >
              <b-spinner small v-if="loading" />
              <span v-else>{{ isEdit ? "更新" : "上傳" }}付款憑證</span>
            </b-button>
            <b-button
              variant="outline-secondary"
              @click="$bvModal.hide('modal-upload-payment-proof')"
            >
              取消
            </b-button>
          </div>
        </b-form>
      </validation-observer>
    </b-overlay>
  </b-modal>
</template>

<script>
import {
  BButton,
  BModal,
  VBModal,
  BOverlay,
  BForm,
  BFormGroup,
  BFormInput,
  BSpinner,
  BRow,
  BCol,
  BFormInvalidFeedback,
  BCard,
  BCardBody,
} from "bootstrap-vue";
import vSelect from "vue-select";
import flatPickr from "vue-flatpickr-component";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import formValidation from "@core/comp-functions/forms/form-validation";
import { ref, computed, watch, getCurrentInstance } from "@vue/composition-api";
import uploadFileSingle from "@/layouts/components/file/uploadFileSingle.vue";
import Ripple from "vue-ripple-directive";
import Swal from "sweetalert2";
import store from "@/store";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

export default {
  name: "ModalUploadPaymentProof",
  components: {
    BButton,
    BModal,
    VBModal,
    BOverlay,
    BForm,
    BFormGroup,
    BFormInput,
    BSpinner,
    BRow,
    BCol,
    BFormInvalidFeedback,
    BCard,
    BCardBody,
    vSelect,
    flatPickr,
    ValidationProvider,
    ValidationObserver,
    uploadFileSingle,
  },
  directives: {
    "b-modal": VBModal,
    Ripple,
  },
  props: {
    billData: {
      type: Object,
      required: true,
    },
    paymentProofData: {
      type: Object,
      default: () => ({}),
    },
    paymentMethodOptions: {
      type: Array,
      required: true,
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
    apiUrl: {
      type: String,
      default: "/suppliers/payment-proofs-multiple",
    },
  },

  setup(props, { emit }) {
    const loading = ref(false);
    const instance = getCurrentInstance();

    // 日期選擇器配置
    const datePickerConfig = {
      dateFormat: "Y-m-d",
      allowInput: true,
      locale: "zh",
    };

    // 付款憑證項目陣列
    const paymentProofItems = ref([
      {
        payment_proof_number: "",
        payment_amount: 0,
        payment_date: "",
        payment_method_id: null,
        check_number: "",
        file: null,
        previewImageData: null,
      },
    ]);

    // 計算屬性
    const formattedBillAmount = computed(() => {
      return props.billData.bill_total_amount
        ? `$${Number(props.billData.bill_total_amount).toFixed(2)}`
        : "$0.00";
    });

    const calculatedTotal = computed(() => {
      return paymentProofItems.value
        .reduce((total, item) => total + (Number(item.payment_amount) || 0), 0)
        .toFixed(2);
    });

    const totalComparisonClass = computed(() => {
      const total = Number(calculatedTotal.value);
      const billTotal = Number(props.billData.bill_total_amount) || 0;

      if (total > billTotal) {
        return "text-danger font-weight-bold";
      } else if (total === billTotal) {
        return "text-success font-weight-bold";
      } else {
        return "text-warning font-weight-bold";
      }
    });

    const showAmountWarning = computed(() => {
      const total = Number(calculatedTotal.value);
      const billTotal = Number(props.billData.bill_total_amount) || 0;
      return total > billTotal;
    });

    // 初始化預設項目的函數
    const createDefaultPaymentProofItem = (billData = null) => ({
      payment_proof_number: "",
      payment_amount: billData?.bill_total_amount || 0,
      payment_date: "",
      payment_method_id: null,
      check_number: "",
      file: null,
      previewImageData: null,
    });

    // 確保陣列始終至少有一個項目
    const ensureMinimumItems = () => {
      if (paymentProofItems.value.length === 0) {
        paymentProofItems.value = [createDefaultPaymentProofItem(props.billData)];
      }
    };

    // 監聽 billData 和 paymentProofData 變化，處理向後兼容性
    watch(
      [() => props.billData, () => props.paymentProofData],
      ([newBillData, newPaymentProofData]) => {
        if (!newBillData) {
          // 如果沒有 billData，確保至少有一個預設項目
          ensureMinimumItems();
          return;
        }

        let hasExistingData = false;

        // 檢查是否有新版多筆付款憑證資料
        if (newBillData.payment_proofs && newBillData.payment_proofs.length > 0) {
          // 使用新版多筆付款憑證資料
          paymentProofItems.value = newBillData.payment_proofs.map(proof => ({
            payment_proof_number: proof.payment_proof_number || "",
            payment_amount: proof.payment_amount || 0,
            payment_date: proof.payment_date || "",
            payment_method_id: proof.payment_method_id || null,
            check_number: proof.check_number || "",
            file: null,
            previewImageData: proof.payment_proof_url ? {
              id: proof.id,
              name: "付款憑證",
              file_url: proof.payment_proof_url,
              file_path: proof.payment_proof_path,
              image_url: proof.payment_proof_url,
              image_path: proof.payment_proof_path,
            } : null,
          }));
          hasExistingData = true;
        } else if (newPaymentProofData && Object.keys(newPaymentProofData).length > 0 &&
                   (newPaymentProofData.payment_proof_number || newPaymentProofData.payment_date || newPaymentProofData.payment_method_id)) {
          // 使用 paymentProofData prop（舊版單一付款憑證資料）
          paymentProofItems.value = [{
            payment_proof_number: newPaymentProofData.payment_proof_number || "",
            payment_amount: newPaymentProofData.payment_amount || newBillData.bill_total_amount || 0,
            payment_date: newPaymentProofData.payment_date || "",
            payment_method_id: newPaymentProofData.payment_method_id || null,
            check_number: newPaymentProofData.check_number || "",
            file: null,
            previewImageData: newPaymentProofData.payment_proof_url ? {
              id: newPaymentProofData.id,
              name: "付款憑證",
              file_url: newPaymentProofData.payment_proof_url,
              file_path: newPaymentProofData.payment_proof_path,
              image_url: newPaymentProofData.payment_proof_url,
              image_path: newPaymentProofData.payment_proof_path,
            } : null,
          }];
          hasExistingData = true;
        } else if (newBillData.payment_proof_number || newBillData.payment_date || newBillData.payment_method_id) {
          // 使用 billData 中的舊版單一付款憑證資料（向後兼容）
          paymentProofItems.value = [{
            payment_proof_number: newBillData.payment_proof_number || "",
            payment_amount: newBillData.payment_amount || newBillData.bill_total_amount || 0,
            payment_date: newBillData.payment_date || "",
            payment_method_id: newBillData.payment_method_id || null,
            check_number: newBillData.check_number || "",
            file: null,
            previewImageData: newBillData.payment_proof_url ? {
              id: newBillData.id,
              name: "付款憑證",
              file_url: newBillData.payment_proof_url,
              file_path: newBillData.payment_proof_path,
              image_url: newBillData.payment_proof_url,
              image_path: newBillData.payment_proof_path,
            } : null,
          }];
          hasExistingData = true;
        }

        // 如果沒有找到任何現有資料，建立預設項目
        if (!hasExistingData) {
          paymentProofItems.value = [createDefaultPaymentProofItem(newBillData)];
        }

        // 確保陣列至少有一個項目
        ensureMinimumItems();
      },
      { immediate: true, deep: true }
    );

    // 方法定義
    const addPaymentProofItem = (index) => {
      paymentProofItems.value.splice(index + 1, 0, createDefaultPaymentProofItem());
    };

    const removePaymentProofItem = (index) => {
      if (paymentProofItems.value.length > 1) {
        paymentProofItems.value.splice(index, 1);
      }
      // 確保至少有一個項目
      ensureMinimumItems();
    };

    const calculateTotalAmount = () => {
      // 計算屬性會自動重新計算，不需要手動觸發
    };

    const validatePaymentProofNumber = async (index) => {
      const item = paymentProofItems.value[index];
      if (!item.payment_proof_number) return;

      try {
        const response = await store.dispatch("supplier/validateMultiplePaymentProofNumber", {
          payment_proof_number: item.payment_proof_number,
        });

        if (!response.data.is_valid) {
          // 這裡需要通過 context 來訪問 $toast
          console.warn("付款憑證編號已存在，請使用其他編號");
        }
      } catch (error) {
        console.error("驗證付款憑證編號失敗:", error);
      }
    };

    const onFileUploaded = (file, index) => {
      paymentProofItems.value[index].file = file;
    };

    const onSubmit = async () => {
      if (showAmountWarning.value) {
        instance.proxy.$toast({
          component: ToastificationContent,
          props: {
            title: "驗證失敗",
            text: "付款憑證總額不能超過帳單總額",
            variant: "danger",
          },
        });
        return;
      }

      // 檢查是否為舊版單一付款憑證的編輯模式
      const isLegacyEdit = props.isEdit &&
        (!props.billData.payment_proofs || props.billData.payment_proofs.length === 0) &&
        (props.billData.payment_proof_number || props.billData.payment_date || props.billData.payment_method_id);

      // 檢查是否只有一筆記錄且為舊版資料格式
      const isLegacyMode = isLegacyEdit || (paymentProofItems.value.length === 1 && !props.billData.payment_proofs);

      if (isLegacyMode) {
        // 使用舊版 API 處理單一付款憑證
        await submitLegacyPaymentProof();
      } else {
        // 使用新版 API 處理多筆付款憑證
        await submitMultiplePaymentProofs();
      }
    };

    const submitLegacyPaymentProof = async () => {
      const item = paymentProofItems.value[0];

      // 驗證檔案（編輯模式下如果已有檔案則不需要重新上傳）
      if (!props.isEdit && !item.file) {
        instance.proxy.$toast({
          component: ToastificationContent,
          props: {
            title: "驗證失敗",
            text: "請上傳付款憑證檔案",
            variant: "danger",
          },
        });
        return;
      }

      const result = await Swal.fire({
        title: "確認上傳",
        text: "確定要上傳付款憑證嗎？",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "確定上傳",
        cancelButtonText: "取消",
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
      });

      if (result.value) {
        loading.value = true;

        try {
          let formData = new FormData();
          formData.append("bill_id", props.billData.id);
          formData.append("payment_date", item.payment_date || "");
          formData.append("payment_method_id", item.payment_method_id || "");
          formData.append("payment_amount", props.billData.bill_total_amount || 0);

          if (item.check_number) {
            formData.append("check_number", item.check_number);
          }
          if (item.payment_proof_number) {
            formData.append("payment_proof_number", item.payment_proof_number);
          }
          if (props.isEdit && props.billData.id) {
            formData.append("id", props.billData.id);
          }
          if (item.file) {
            formData.append("file", item.file);
          }

          // 使用舊版 API 端點
          const legacyApiUrl = "/suppliers/payment-proofs";
          const response = await instance.proxy.$http.post(legacyApiUrl, formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          });

          instance.proxy.$toast({
            component: ToastificationContent,
            props: {
              title: "成功",
              text: response.data.message,
              variant: "success",
            },
          });

          emit("refetch-data");
          instance.proxy.$bvModal.hide("modal-upload-payment-proof");

        } catch (error) {
          console.error("上傳失敗:", error);
          instance.proxy.$toast({
            component: ToastificationContent,
            props: {
              title: "上傳失敗",
              text: error.response?.data?.message || "上傳付款憑證時發生錯誤",
              variant: "danger",
            },
          });
        } finally {
          loading.value = false;
        }
      }
    };

    const submitMultiplePaymentProofs = async () => {
      // 驗證所有項目都有檔案
      for (let i = 0; i < paymentProofItems.value.length; i++) {
        if (!paymentProofItems.value[i].file && !paymentProofItems.value[i].previewImageData) {
          instance.proxy.$toast({
            component: ToastificationContent,
            props: {
              title: "驗證失敗",
              text: `請為付款憑證 ${i + 1} 上傳檔案`,
              variant: "danger",
            },
          });
          return;
        }
      }

      const result = await Swal.fire({
        title: "確認上傳",
        text: `確定要上傳 ${paymentProofItems.value.length} 筆付款憑證嗎？`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "確定上傳",
        cancelButtonText: "取消",
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
      });

      if (result.value) {
        loading.value = true;

        try {
          let formData = new FormData();
          formData.append("bill_id", props.billData.id);

          // 添加付款憑證陣列資料
          paymentProofItems.value.forEach((item, index) => {
            formData.append(`payment_proofs[${index}][payment_proof_number]`, item.payment_proof_number || "");
            formData.append(`payment_proofs[${index}][payment_amount]`, item.payment_amount || 0);
            formData.append(`payment_proofs[${index}][payment_date]`, item.payment_date || "");
            formData.append(`payment_proofs[${index}][payment_method_id]`, item.payment_method_id || "");
            if (item.check_number) {
              formData.append(`payment_proofs[${index}][check_number]`, item.check_number);
            }
            if (item.file) {
              formData.append(`payment_proofs[${index}][file]`, item.file);
            }
          });

          const response = await instance.proxy.$http.post(props.apiUrl, formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          });

          instance.proxy.$toast({
            component: ToastificationContent,
            props: {
              title: "成功",
              text: response.data.message,
              variant: "success",
            },
          });

          emit("refetch-data");
          instance.proxy.$bvModal.hide("modal-upload-payment-proof");

        } catch (error) {
          console.error("上傳失敗:", error);
          instance.proxy.$toast({
            component: ToastificationContent,
            props: {
              title: "上傳失敗",
              text: error.response?.data?.message || "上傳付款憑證時發生錯誤",
              variant: "danger",
            },
          });
        } finally {
          loading.value = false;
        }
      }
    };

    // 重置表單資料的函數
    const resetFormData = () => {
      paymentProofItems.value = [createDefaultPaymentProofItem(props.billData)];
      loading.value = false;
    };

    const { refFormObserver, getValidationState, resetForm } =
      formValidation(resetFormData);

    return {
      loading,
      datePickerConfig,
      paymentProofItems,
      formattedBillAmount,
      calculatedTotal,
      totalComparisonClass,
      showAmountWarning,
      addPaymentProofItem,
      removePaymentProofItem,
      calculateTotalAmount,
      validatePaymentProofNumber,
      onFileUploaded,
      onSubmit,
      submitLegacyPaymentProof,
      submitMultiplePaymentProofs,
      createDefaultPaymentProofItem,
      ensureMinimumItems,
      resetFormData,
      refFormObserver,
      getValidationState,
      resetForm,
    };
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";
</style>